/**
 * Mock API adapter that replaces axios in mock mode.
 * Returns canned data based on URL pattern matching.
 */

import {
  mockAnnouncements,
  mockCompetitions,
  mockLiveScores,
  mockMedalTally,
  mockSchedule,
  mockVenues,
  mockStats,
  mockMedia,
  mockAthleteProfile,
  mockAthleteEvent,
  mockAthleteCompetitions,
} from './mockData';

// Simulate network delay (ms)
const DELAY = 200;

function delay<T>(data: T): Promise<{ data: T }> {
  return new Promise((resolve) => setTimeout(() => resolve({ data }), DELAY));
}

function matchUrl(url: string, pattern: string): boolean {
  // Simple path matching - strip query params first
  const path = url.split('?')[0];
  if (pattern.endsWith('*')) {
    return path.startsWith(pattern.slice(0, -1));
  }
  return path === pattern;
}

function getQueryParam(url: string, key: string): string | null {
  const match = url.match(new RegExp(`[?&]${key}=([^&]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getPathSegment(url: string, index: number): string {
  const path = url.split('?')[0];
  const segments = path.split('/').filter(Boolean);
  return segments[index] ?? '';
}

async function mockGet(url: string) {
  // --- Announcements ---
  if (matchUrl(url, '/announcements') && !url.includes('/announcements/')) {
    const page = Number(getQueryParam(url, 'page') || '1');
    const category = getQueryParam(url, 'category');
    let items = mockAnnouncements;
    if (category && category !== 'all') {
      items = items.filter((a) => a.category === category);
    }
    const pageSize = 6;
    const start = (page - 1) * pageSize;
    const paged = items.slice(start, start + pageSize);
    return delay({
      data: paged,
      total: items.length,
      page,
      totalPages: Math.ceil(items.length / pageSize),
    });
  }

  // --- Announcement detail ---
  if (url.match(/\/announcements\/[\w-]+/)) {
    const id = getPathSegment(url, 1);
    const item = mockAnnouncements.find((a) => a.id === id) ?? mockAnnouncements[0];
    return delay({ data: item });
  }

  // --- Competitions ---
  if (matchUrl(url, '/competitions') && !url.includes('/competitions/')) {
    return delay({ data: mockCompetitions });
  }

  // --- Competition detail ---
  if (url.match(/\/competitions\/[\w-]+/)) {
    const id = getPathSegment(url, 1);
    const item = mockCompetitions.find((c) => c.id === id) ?? mockCompetitions[0];
    return delay({ data: item });
  }

  // --- Live scores ---
  if (matchUrl(url, '/results/live')) {
    const compId = getQueryParam(url, 'competitionId');
    let scores = mockLiveScores;
    if (compId && compId !== 'all') {
      scores = scores.filter((s) => s.competitionId === compId);
    }
    return delay({ data: scores });
  }

  // --- Medal tally ---
  if (matchUrl(url, '/results/medals')) {
    return delay({ data: mockMedalTally });
  }

  // --- Schedule ---
  if (matchUrl(url, '/schedule')) {
    return delay({ data: mockSchedule });
  }

  // --- Venues ---
  if (matchUrl(url, '/venues') && !url.includes('/venues/')) {
    return delay({ data: mockVenues });
  }

  // --- Venue detail ---
  if (url.match(/\/venues\/[\w-]+/)) {
    const id = getPathSegment(url, 1);
    const item = mockVenues.find((v) => v.id === id) ?? mockVenues[0];
    return delay({ data: item });
  }

  // --- Stats ---
  if (matchUrl(url, '/stats')) {
    return delay({ data: mockStats });
  }

  // --- Media ---
  if (matchUrl(url, '/media')) {
    const type = getQueryParam(url, 'type');
    let items = mockMedia;
    if (type) {
      items = items.filter((m) => m.type === type);
    }
    return delay({ data: items });
  }

  // --- Athlete profile ---
  if (matchUrl(url, '/me/profile')) {
    return delay({ data: mockAthleteProfile });
  }

  // --- Athlete event ---
  if (matchUrl(url, '/me/event')) {
    return delay({ data: mockAthleteEvent });
  }

  // --- Athlete competitions ---
  if (matchUrl(url, '/me/competitions')) {
    return delay({ data: mockAthleteCompetitions });
  }

  // Fallback
  console.warn('[MockAPI] Unmatched GET:', url);
  return delay({ data: null });
}

async function mockPost(url: string, _body?: unknown) {
  // --- Athlete login (accept any credentials) ---
  if (matchUrl(url, '/athlete-auth/login')) {
    return delay({
      data: {
        token: 'mock-athlete-token-12345',
        mustChangePassword: false,
      },
    });
  }

  console.warn('[MockAPI] Unmatched POST:', url);
  return delay({ data: null });
}

async function mockPut(url: string, _body?: unknown) {
  // --- Change password ---
  if (matchUrl(url, '/athlete-auth/change-password')) {
    return delay({
      data: {
        token: 'mock-athlete-token-updated',
        message: 'Password changed successfully',
      },
    });
  }

  // --- Update profile ---
  if (matchUrl(url, '/me/profile')) {
    return delay({ data: mockAthleteProfile });
  }

  console.warn('[MockAPI] Unmatched PUT:', url);
  return delay({ data: null });
}

/**
 * Drop-in replacement for the axios instance used in useApi.ts.
 * Exposes .get(), .post(), .put() with the same return shape.
 */
export const mockApi = {
  get: (url: string, _config?: unknown) => mockGet(url),
  post: (url: string, body?: unknown, _config?: unknown) => mockPost(url, body),
  put: (url: string, body?: unknown, _config?: unknown) => mockPut(url, body),
  defaults: { baseURL: '/api/v1' },
  interceptors: {
    request: { use: () => 0, eject: () => {} },
    response: { use: () => 0, eject: () => {} },
  },
};
